import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { paginatedSchema } from "../pagination";

import { baseAssetWithTimeseriesSchema } from "./assetsBase";

export const weirListItemSchema = baseAssetWithTimeseriesSchema.extend({
  name: z.string(),
  type: z.string(),
  crest_type: z.string(),
  crest_width: z.number().nullable(),
  crest_level: z.number().nullable(),
  discharge_coeff: z.number().nullable(),
  lat_dis_coeff: z.number().nullable(),
  allowed_flow_direction: z.string().nullable() /* backwards | forwards | both | closed */,
  controlled: z.number().int().nullable(),
  angle: z.number().nullable(),
  comment: z.string().nullable(),
});

export type WeirListItem = z.infer<typeof weirListItemSchema>;

export const weirListSchema = paginatedSchema(weirListItemSchema);
export type WeirList = z.infer<typeof weirListSchema>;

export const weirSchema = weirListItemSchema;
export type Weir = z.infer<typeof weirSchema>;

export function useWeirs(params = {}) {
  const url = combineUrlAndParams("/api/v4/weirs/", params);
  return useParsedQuery<WeirList>(
    ["lizard-api-v4", "weirs", url],
    () => fetchWithError(url),
    (json: unknown) => weirListSchema.parse(json)
  );
}

export function useWeir(id: number) {
  return useParsedQuery<Weir>(
    ["lizard-api-v4", "weirs", id],
    () => fetchWithError(`/api/v4/weirs/${id}/`),
    (json: unknown) => weirSchema.parse(json)
  );
}

export function useMultipleWeirs(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "weirs", id],
        fetchFunction: () => fetchWithError(`/api/v4/weirs/${id}/`),
        parseFunction: (json: unknown) => weirSchema.parse(json),
      };
    })
  );
}
